import React, { useEffect, useState } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import {
  Loader2, Save, Eye, EyeOff, Cookie, BarChart3, Target,
  ExternalLink, Info, AlertCircle, Code2,
} from 'lucide-react';

function SecretInput({ value, onChange, placeholder, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-2.5 pr-10 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-[#D44E20]"
      >
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

function Field({ label, hint, helpLink, helpLabel, children, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid ? `field-${testid}` : undefined}>
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-medium text-[#1A1A1A]">{label}</label>
        {helpLink && (
          <a href={helpLink} target="_blank" rel="noreferrer"
             className="text-[11px] text-[#3B82F6] hover:underline inline-flex items-center gap-1">
            {helpLabel || 'Dove lo trovo?'} <ExternalLink size={10} />
          </a>
        )}
      </div>
      {children}
      {hint && <p className="text-[11px] text-[#94A3B8] leading-snug">{hint}</p>}
    </div>
  );
}

function Card({ title, icon: Icon, color = '#D44E20', children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
          <Icon size={16} style={{ color }} />
        </div>
        <h2 className="text-sm font-semibold text-[#1A1A1A]">{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

export default function AdminTracking() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState({
    gtm_container_id: '',
    ga4_measurement_id: '',
    ga4_api_secret: '',
    meta_pixel_id: '',
    meta_capi_access_token: '',
    meta_test_event_code: '',
    tiktok_pixel_id: '',
    tiktok_capi_access_token: '',
    tiktok_test_event_code: '',
    cookie_policy_md: '',
    cookie_consent_version: 1,
  });

  useEffect(() => {
    adminApi.get('/admin/tracking')
      .then(r => setCfg(r.data))
      .catch(() => toast.error('Errore nel caricamento configurazione'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await adminApi.put('/admin/tracking', cfg);
      setCfg(r.data.tracking);
      toast.success('Configurazione tracking salvata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const bumpConsentVersion = () => {
    setCfg(c => ({ ...c, cookie_consent_version: (c.cookie_consent_version || 1) + 1 }));
    toast.info('Versione consenso incrementata. Salva per chiedere di nuovo il consenso a tutti i visitatori.');
  };

  const resetCookiePolicyToDefault = () => {
    if (cfg.default_cookie_policy_md) {
      setCfg(c => ({ ...c, cookie_policy_md: cfg.default_cookie_policy_md }));
      toast.success('Testo ripristinato al template di default. Ricordati di salvare.');
    } else {
      toast.error('Template di default non disponibile');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="animate-spin text-[#D44E20]" size={28} />
    </div>
  );

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-4xl" data-testid="admin-tracking-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Tracking & Pixel</h1>
          <p className="text-[#64748B] text-sm mt-1">
            Misura le conversioni delle tue campagne ads su Google, Meta, TikTok. Configura una volta, lascia che gli eventi si propaghino automaticamente da ogni acquisto online.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-tracking-btn"
          className="flex-shrink-0 inline-flex items-center gap-2 bg-[#D44E20] hover:bg-[#B33F18] text-white text-sm font-semibold px-5 py-2.5 rounded-xl disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
          {saving ? 'Salvataggio…' : 'Salva'}
        </button>
      </div>

      {/* Info banner */}
      <div className="mb-6 bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3 items-start text-sm text-blue-900">
        <Info size={16} className="flex-shrink-0 mt-0.5 text-blue-600" />
        <div className="leading-relaxed">
          <p className="font-semibold mb-0.5">Come funziona</p>
          <p className="text-blue-800/90">
            Compila <b>solo i campi che ti servono</b>. Se inserisci un <b>GTM Container ID</b>, tutto il resto si gestisce da GTM (consigliato per chi vuole pixel multipli o conversione granulari). In alternativa puoi compilare i singoli ID di GA4 / Meta / TikTok e noi li carichiamo direttamente.
            <br />Gli eventi tracciati sono: <b>view_item</b>, <b>begin_checkout</b>, <b>add_payment_info</b>, <b>purchase</b> — formato GA4 enhanced ecommerce con auto-mapping ai pixel.
          </p>
        </div>
      </div>

      <div className="space-y-5">
        {/* GTM */}
        <Card title="Google Tag Manager (consigliato)" icon={Code2} color="#1E88E5" testid="card-gtm">
          <Field
            label="GTM Container ID"
            hint="Se compilato, viene caricato GTM e tutti gli altri pixel/tag puoi gestirli da lì. Lasciaci vuoto se preferisci i pixel diretti qui sotto."
            helpLink="https://support.google.com/tagmanager/answer/6103696"
            testid="gtm"
          >
            <input
              value={cfg.gtm_container_id}
              onChange={e => setCfg(c => ({ ...c, gtm_container_id: e.target.value }))}
              placeholder="GTM-XXXXXX"
              data-testid="input-gtm-id"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
            />
          </Field>
        </Card>

        {/* GA4 */}
        <Card title="Google Analytics 4" icon={BarChart3} color="#F9AB00" testid="card-ga4">
          <Field
            label="Measurement ID"
            hint="Formato G-XXXXXXXXX. Caricato solo se GTM non è configurato."
            helpLink="https://support.google.com/analytics/answer/9539598"
            testid="ga4-id"
          >
            <input
              value={cfg.ga4_measurement_id}
              onChange={e => setCfg(c => ({ ...c, ga4_measurement_id: e.target.value }))}
              placeholder="G-XXXXXXXXX"
              data-testid="input-ga4-id"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
            />
          </Field>
          <Field
            label="Measurement Protocol API Secret"
            hint="Permette di inviare gli eventi 'purchase' server-side (più affidabile su iOS / ad blocker). Trovi il secret in GA4 → Admin → Data Streams → Web stream → Measurement Protocol API secrets."
            helpLink="https://developers.google.com/analytics/devguides/collection/protocol/ga4/sending-events"
            testid="ga4-secret"
          >
            <SecretInput
              value={cfg.ga4_api_secret}
              onChange={v => setCfg(c => ({ ...c, ga4_api_secret: v }))}
              placeholder="abc123XYZ..."
              testId="input-ga4-secret"
            />
          </Field>
        </Card>

        {/* Meta */}
        <Card title="Meta Pixel (Facebook / Instagram Ads)" icon={Target} color="#1877F2" testid="card-meta">
          <Field
            label="Pixel ID"
            hint="Numero di 15-16 cifre. Trovi questo ID in Meta Business Suite → Events Manager → Data sources."
            helpLink="https://www.facebook.com/business/help/952192354843755"
            testid="meta-id"
          >
            <input
              value={cfg.meta_pixel_id}
              onChange={e => setCfg(c => ({ ...c, meta_pixel_id: e.target.value }))}
              placeholder="123456789012345"
              data-testid="input-meta-pixel-id"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
            />
          </Field>
          <Field
            label="Conversions API Access Token"
            hint="Fondamentale per ottimizzare le campagne in era iOS 14.5+. Eventi inviati server-side, deduplicati con il pixel browser. Genera il token in Events Manager → Settings → Conversions API."
            helpLink="https://developers.facebook.com/docs/marketing-api/conversions-api/get-started"
            testid="meta-capi"
          >
            <SecretInput
              value={cfg.meta_capi_access_token}
              onChange={v => setCfg(c => ({ ...c, meta_capi_access_token: v }))}
              placeholder="EAAxxxxxxx..."
              testId="input-meta-capi-token"
            />
          </Field>
          <Field
            label="Test Event Code (opzionale)"
            hint="Imposta questo codice solo durante i test. Trovi gli eventi in Events Manager → Test Events. Rimuovi quando vai live."
            testid="meta-test"
          >
            <input
              value={cfg.meta_test_event_code}
              onChange={e => setCfg(c => ({ ...c, meta_test_event_code: e.target.value }))}
              placeholder="TEST12345"
              data-testid="input-meta-test-code"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
            />
          </Field>
        </Card>

        {/* TikTok */}
        <Card title="TikTok Pixel" icon={Target} color="#000000" testid="card-tiktok">
          <Field
            label="Pixel ID"
            hint="Formato 'CABCDEFGHIJKLMNO'. Lo trovi in TikTok Ads Manager → Assets → Events → Web Events."
            helpLink="https://ads.tiktok.com/help/article/get-started-pixel"
            testid="tiktok-id"
          >
            <input
              value={cfg.tiktok_pixel_id}
              onChange={e => setCfg(c => ({ ...c, tiktok_pixel_id: e.target.value }))}
              placeholder="CABCDEFGHIJKLMNOPQRST"
              data-testid="input-tiktok-pixel-id"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
            />
          </Field>
          <Field
            label="Events API Access Token"
            hint="Server-side per affidabilità massima. TikTok Ads Manager → Assets → Events → Manage → Events API."
            helpLink="https://business-api.tiktok.com/portal/docs?id=1771101303285761"
            testid="tiktok-capi"
          >
            <SecretInput
              value={cfg.tiktok_capi_access_token}
              onChange={v => setCfg(c => ({ ...c, tiktok_capi_access_token: v }))}
              placeholder="xxxxx..."
              testId="input-tiktok-capi-token"
            />
          </Field>
          <Field
            label="Test Event Code (opzionale)"
            hint="Imposta durante i test. Vedi gli eventi in Test Events di TikTok Ads Manager. Rimuovi quando vai live."
            testid="tiktok-test"
          >
            <input
              value={cfg.tiktok_test_event_code}
              onChange={e => setCfg(c => ({ ...c, tiktok_test_event_code: e.target.value }))}
              placeholder="TEST12345"
              data-testid="input-tiktok-test-code"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
            />
          </Field>
        </Card>

        {/* Cookie policy */}
        <Card title="Cookie Consent & Policy" icon={Cookie} color="#10B981" testid="card-cookie-policy">
          <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
            <div className="flex items-start gap-2 text-amber-900 text-sm">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-amber-600" />
              <p>
                Versione consenso attuale: <b data-testid="consent-version">{cfg.cookie_consent_version || 1}</b>.
                Modificare il testo qui sotto incrementerà automaticamente la versione e chiederà di nuovo il consenso ai visitatori.
              </p>
            </div>
            <button
              onClick={bumpConsentVersion}
              data-testid="bump-consent-version-btn"
              className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 whitespace-nowrap px-3 py-1.5 rounded-lg border border-amber-200 hover:bg-amber-100"
            >
              Forza re-consent
            </button>
          </div>
          <Field
            label="Testo Cookie Policy (Markdown)"
            hint="Visibile ai visitatori quando cliccano 'Maggiori informazioni' nel banner cookie. Markdown supportato (headings, liste, link)."
            testid="cookie-policy-md"
          >
            <div className="flex justify-end mb-1">
              <button
                type="button"
                onClick={resetCookiePolicyToDefault}
                data-testid="reset-cookie-policy-btn"
                className="text-[11px] font-semibold text-slate-500 hover:text-[#D44E20] underline"
              >
                Ripristina template di default
              </button>
            </div>
            <textarea
              value={cfg.cookie_policy_md}
              onChange={e => setCfg(c => ({ ...c, cookie_policy_md: e.target.value }))}
              rows={14}
              data-testid="input-cookie-policy-md"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono leading-relaxed"
            />
          </Field>
        </Card>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-tracking-btn-bottom"
          className="inline-flex items-center gap-2 bg-[#D44E20] hover:bg-[#B33F18] text-white text-sm font-semibold px-6 py-3 rounded-xl disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
          {saving ? 'Salvataggio…' : 'Salva configurazione'}
        </button>
      </div>
    </div>
  );
}
